<template>
	<section class="daily-rewards-popup">
		<div class="daily-rewards-popup__rewards">
			<RaggedBoxBackground
				slug="daily-rewards-box-bg"
				:skin="{ stopColor1: '#f2dcb5', stopColor2: '#fff3dd' }"
			/>
			<div
				class="daily-rewards"
				v-if="rewards && Object.keys(rewards).length"
			>
				<template
					v-for="([icon, value], index) in Object.entries(rewards)"
					:key="index"
				>
					<div class="daily-reward" v-if="value > 0">
						<span class="daily-reward__value">
							{{
								`${value >= 0 ? "+" : ""}${formatNumber(value)}`
							}}
						</span>
						<AppMainIcon
							class="daily-reward__icon"
							:iconName="icon.toLowerCase()"
							:iconSize="34"
						/>
					</div>
				</template>
			</div>
		</div>
		<AppTextButton
			class="daily-rewards-popup__btn"
			:type="StatusTypes.SUCCESS"
			:text="translate('button.claim')"
			@click="claimDailyReward"
		/>
		<template v-if="recommendedUpgrade">
			<DividerLine />
			<div class="daily-rewards-popup__upgrade">
				<div class="daily-rewards-popup__upgrade-heading">
					{{ translate("ui.recommended_upgrades") }}
				</div>
				<UpgradeBox
					@click="toUpgrades"
					:has-redirect="true"
					:skin="SkinTypes.LIGHT"
					:id="recommendedUpgrade[0]"
					:heading="recommendedUpgrade[1]?.heading.toString()"
					:description="recommendedUpgrade[1]?.description"
					:descriptionReplace="
						recommendedUpgrade[1]?.descriptionReplace
					"
				/>
			</div>
		</template>
	</section>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import DividerLine from "@/components/SVG/DividerLine.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import { saveGrandPrizeState, useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import type { UpgradeId, UpgradeItem } from "@/game/entities/config/upgrades";
import { UpgradeIds, upgrades } from "@/game/entities/config/upgrades";
import { levelManager } from "@/game/LevelManager";
import { TileToastType, ToastPlacement } from "@/game/ToastMessage";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { RecordedActivity } from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { EventCategory, SkinTypes, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

const claimGuard = useRewardClaimGuard();

const emit = defineEmits(["close"]);
const { setLastRecordedActivity } = useRuntimeStore();
const {
	activeProvisions,
	setLastDailyRewardPickedDaysAfterReg,
	daysAfterRegistration,
	setIncomeBoostsUsedToday,
	setIncomeBoostTicksRemaining,
	setGemMinesClaimable,
} = useUserStore();
const resourcesStore = useResourcesStore();
const settlementsStore = useSettlementsStore();
const upgradesStore = useUpgradesStore();
const runtimeStore = useRuntimeStore();

const socketService = markRaw(useSocketService());

const eventStore = useEventStore();
const { attributes, grandPrizeState, isActive } = storeToRefs(eventStore);

const recommendedUpgradeId = 0; // TODO nastavit spravne ked bude pripraveny upgrade

const blocked = ref<boolean>(false);

const rewards = computed((): Partial<Record<ResourceType, number>> => {
	// 1. Basic reward including messenger upgrade
	const baseReward =
		10 * (1 + getUpgradeCurrentReplaceValueById(UpgradeIds.messenger));

	// 2. Number of active provisions
	const activeProvisionsCount = (activeProvisions ?? []).length;

	// 3. Linear multiplier: 0 provisions = x1, 1 provision = x2 (+100%), 2 provisions = x3 (+200%), etc.
	const multiplier = activeProvisionsCount + 1;

	const finalReward = baseReward * multiplier;

	return {
		[ResourceType.GEMS]: finalReward,
		[ResourceType.SUPER_CLICKER]: finalReward,
	};
});

const emitter = markRaw(useEmitter());

const recommendedUpgrade = computed((): [UpgradeId, UpgradeItem] | null => {
	if (!recommendedUpgradeId) return null;

	const filteredUpgrade =
		Array.from(upgrades.entries()).find(
			(upgrade: [UpgradeId, UpgradeItem]): boolean =>
				upgrade[0] === recommendedUpgradeId &&
				upgrade[1].levelUnlock <= settlementsStore.mainLevel &&
				upgradesStore[upgrade[0]] < upgrade[1].maxLevel,
		) ?? null;
	if (!filteredUpgrade) return null;
	return filteredUpgrade;
});

const claimDailyReward = (): Promise<void> =>
	claimGuard.guard(async (): Promise<void> => {
		if (blocked.value) return;
		blocked.value = true;
		(
			Object.entries(rewards.value) as Array<[ResourceType, number]>
		).forEach(([resource, value]: [ResourceType, number]): void => {
			resourcesStore.increase(resource, value, "daily reward");
		});

		setLastDailyRewardPickedDaysAfterReg(
			floorNumber(daysAfterRegistration),
		);
		setIncomeBoostsUsedToday(0);
		setIncomeBoostTicksRemaining(0);
		runtimeStore.setIncomeBoostActive(false);
		runtimeStore.setInfoNotifications(1);
		setGemMinesClaimable(true);
		emitter.emit(EmitterEvents.PhaserCreateGemsBadge);

		await socketService.emitSaveRequest({
			stores: [EmitStores.RESOURCES, EmitStores.USER],
		});

		setLastRecordedActivity(RecordedActivity.DAILY_REWARD_CLOSED);

		if (
			isActive &&
			attributes.value.type &&
			[EventCategory.SOLAR, EventCategory.PIRATES].includes(
				attributes.value.type,
			) &&
			grandPrizeState.value
		) {
			const nextGPState = {
				gemsEarnedToday: 0,
				chestKeysEarnedToday: 0,
			};
			eventStore.mergeGrandPrizeState(nextGPState);
			const ok = await saveGrandPrizeState({
				eventGrandPrizeState: nextGPState,
			});
			if (!ok) {
				useEmitter().emit(EmitterEvents.PhaserTextMessage, {
					text: translate("ui.connection_error_info"),
					type: TileToastType.WARNING,
					placement: ToastPlacement.TOP_CENTER,
				});
			}
		}

		levelManager.resetDailyQuests();

		emit("close");
		blocked.value = false;
	});

const toUpgrades = (): void => {
	if (!claimGuard.requireConnection()) return;
	setLastRecordedActivity(RecordedActivity.DAILY_REWARD_CLOSED);
	claimDailyReward();
};
</script>

<style lang="scss" scoped>
.daily-rewards-popup {
	@include flex-col-center;
	&__rewards {
		position: relative;
		padding: 1rem 0.75rem;
		min-height: 5.75rem;
		@include flex-col-center;
		margin-top: 0.85rem;
		margin-bottom: 0.85rem;
		width: 95%;
	}

	.daily-rewards {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.5rem;
		width: 80%;
	}
	.daily-reward {
		@include flex-row-center;
		gap: 0.15rem;
		&__value {
			color: #372615;
			font-weight: bold;
			font-size: 2rem;
		}
	}
	&__btn {
		margin-bottom: 1rem;
	}

	&__upgrade {
		@include flex-col-center;
		gap: 0.25rem;
		background-color: #27282b;
		padding: 0.85rem;
		padding-top: 0;
		width: 100%;

		&-heading {
			color: #f2dcb5;
			font-weight: bold;
			font-size: 1rem;
		}
	}

	:deep(.upgrade-box__heading) {
		align-items: initial;

		& > span {
			top: 0.2rem;
		}
	}

	:deep(.upgrade-box__content) {
		position: relative;
		bottom: 0.25rem;
		padding-right: 0;
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		z-index: -1;
		filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
		width: 100%;
		height: 100%;
	}

	:deep(.divider-line) {
		position: absolute;
		bottom: 7rem;
		width: 100%;
	}
}
</style>
